import React, { useState, useEffect } from 'react';
import '../styles/Settings.css';
import '../styles/EmployeeManagement.css';
import api from '../services/api';
import adminApi from '../services/adminApi';
import { useUser } from '../contexts/UserContext';
import EmployeeTemplateForm from './EmployeeTemplateForm';

// Konstanter
const MONTHS = [
  "Januar", "Februar", "Marts", "April", "Maj", "Juni",
  "Juli", "August", "September", "Oktober", "November", "December"
];

const STATUS_TYPES = {
  PENDING: "Afventer",
  ACTIVE: "Aktiv",
  CLOSED: "Lukket"
};

// eslint-disable-next-line no-unused-vars
const CHANGE_TYPES = {
  ADDRESS: "address",
  BANK: "bank",
  TAX: "tax",
  POSITION: "position",
  DEPARTMENT: "department"
};

const SECTION_LABELS = {
  stamdata: "Stamdata",
  bank: "Bank- og skatteoplysninger",
  employment: "Ansættelsesdata",
  setup: "Opsætning",
  salary: "Løn",
  benefits: "Personalegoder",
  vacation: "Ferieordning",
  notes: "Noter"
};

// Hjælpefunktioner
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setHours(date.getHours() + 2); // Tilføj 2 timer for at kompensere for tidszonen
  return date.toISOString().split('T')[0];
};

const formatDateForServer = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setHours(12); // Sæt tidspunktet til midt på dagen for at undgå tidszonekonflikter
  return date.toISOString();
};

const translateRole = (role) => {
  const translations = {
    'admin': 'Administrator',
    'consultant': 'Konsulent',
    'client': 'Kunde'
  };
  return translations[role] || role;
};

// Loading og Error komponenter
const LoadingSpinner = () => (
  <div className="loading">
    <i className="fas fa-spinner fa-spin"></i>
    <span>Indlæser...</span>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="error-message">
    <i className="fas fa-exclamation-circle"></i>
    <span>{message}</span>
  </div>
);

function Settings() {
  const [activeTab, setActiveTab] = useState('kundeoplysninger');
  const { user } = useUser();
  const userPermissions = user ? {
    isAdmin: user.role === 'admin',
    isConsultant: user.role === 'consultant',
    isClient: user.role === 'client',
    canManageUsers: user.role === 'admin' || user.role === 'consultant',
    canManagePaymentForms: user.role === 'admin' || user.role === 'consultant',
    canManagePayRuns: user.role === 'admin' || user.role === 'consultant',
    canManageEmployeeFields: user.role === 'admin' || user.role === 'consultant',
    canManagePayrollTypes: user.role === 'admin' || user.role === 'consultant',
    canManageTemplates: user.role === 'admin' || user.role === 'consultant'
  } : {
    isAdmin: false,
    isConsultant: false,
    isClient: false,
    canManageUsers: false,
    canManagePaymentForms: false,
    canManagePayRuns: false,
    canManageEmployeeFields: false,
    canManagePayrollTypes: false,
    canManageTemplates: false
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'kundeoplysninger':
        return <KundeoplysningerTab />;
      case 'brugeradgang':
        return userPermissions.canManageUsers ? <BrugeradgangTab /> : null;
      case 'afregningsform':
        return userPermissions.canManagePaymentForms ? <AfregningsformTab /> : null;
      case 'medarbejderfelter':
        return userPermissions.canManageEmployeeFields ? <MedarbejderfelterTab /> : null;
      case 'skabeloner':
        return userPermissions.canManageTemplates ? <SkabelonerTab /> : null;
      case 'lonarter':
        return userPermissions.canManagePayrollTypes ? <LonarterTab /> : null;
      case 'lonperioder':
        return userPermissions.canManagePayRuns ? <LonkorslerTab /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="settings">
      <h2>Indstillinger</h2>
      <div className="tabs">
        <button 
          onClick={() => setActiveTab('kundeoplysninger')} 
          className={activeTab === 'kundeoplysninger' ? 'active' : ''}
        >
          Kundeoplysninger
        </button>
        {userPermissions.canManageUsers && (
          <button 
            onClick={() => setActiveTab('brugeradgang')} 
            className={activeTab === 'brugeradgang' ? 'active' : ''}
          >
            Brugeradgang
          </button>
        )}
        {userPermissions.canManagePaymentForms && (
          <button 
            onClick={() => setActiveTab('afregningsform')} 
            className={activeTab === 'afregningsform' ? 'active' : ''}
          >
            Afregningsform
          </button>
        )}
        {userPermissions.canManageEmployeeFields && (
          <button 
            onClick={() => setActiveTab('medarbejderfelter')} 
            className={activeTab === 'medarbejderfelter' ? 'active' : ''}
          >
            Medarbejderfelter
          </button>
        )}
        {userPermissions.canManageTemplates && (
          <button 
            onClick={() => setActiveTab('skabeloner')} 
            className={activeTab === 'skabeloner' ? 'active' : ''}
          >
            Skabeloner
          </button>
        )}
        {userPermissions.canManagePayrollTypes && (
          <button 
            onClick={() => setActiveTab('lonarter')} 
            className={activeTab === 'lonarter' ? 'active' : ''}
          >
            Lønarter
          </button>
        )}
        {userPermissions.canManagePayRuns && (
          <button 
            onClick={() => setActiveTab('lonperioder')} 
            className={activeTab === 'lonperioder' ? 'active' : ''}
          >
            Lønkørsler
          </button>
        )}
      </div>
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
}

const styles = `
  .settings h2 {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .settings h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .settings h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

function KundeoplysningerTab() {
  const [kundeData, setKundeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchKundeData = async () => {
      try {
        const data = await api.get('/info');
        setKundeData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchKundeData();
  }, []);

  const handleNemKontoChange = async (e) => {
    try {
      setIsSaving(true);
      const newValue = e.target.checked;
      await api.put('/info', { use_nemkonto: newValue });
      setKundeData(prev => ({ ...prev, use_nemkonto: newValue }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!kundeData) return <ErrorMessage message="Ingen kundeoplysninger fundet" />;

  return (
    <div className="kundeoplysninger-container">
      <div className="kundeoplysninger-info">
        <h3>Kundeoplysninger</h3>
        <p><strong>Navn:</strong> {kundeData.name}</p>
        <p><strong>CVR-nr.:</strong> {kundeData.cvr}</p>
        <p><strong>Kontaktperson:</strong> {kundeData.contact_person}</p>
        <p><strong>E-mail:</strong> {kundeData.email}</p>
        <p><strong>Telefon:</strong> {kundeData.phone}</p>
        <p><strong>Oprettet:</strong> {formatDate(kundeData.created_at)}</p>
        <p><strong>Sidst opdateret:</strong> {formatDate(kundeData.updated_at)}</p>
        <div className="nemkonto-setting">
          <label>
            <input
              type="checkbox"
              checked={kundeData.use_nemkonto}
              onChange={handleNemKontoChange}
              disabled={isSaving}
            />
            Anvend NemKonto som standard for nye medarbejdere
          </label>
          {isSaving && <span className="saving-indicator">Gemmer...</span>}
        </div>
      </div>
    </div>
  );
}

function BrugeradgangTab() {
  const [brugere, setBrugere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBruger, setNewBruger] = useState({
    email: '',
    name: '',
    role: 'client'
  });
  const { user } = useUser();

  useEffect(() => {
    const fetchBrugere = async () => {
      try {
        const data = await adminApi.getClientUsers(user.clientId);
        setBrugere(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBrugere();
  }, [user.clientId]);

  const handleAddBruger = async () => {
    try {
      const tempPassword = 'DinLon2024!'; // Midlertidigt password
      const data = await adminApi.createClientUser(user.clientId, {
        ...newBruger,
        password: tempPassword
      });
      setBrugere([...brugere, { ...data, active: true }]);
      setShowAddModal(false);
      setNewBruger({ email: '', name: '', role: 'client' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeactivateBruger = async (brugerId) => {
    try {
      await adminApi.deleteClientUser(user.clientId, brugerId);
      setBrugere(brugere.map(bruger => 
        bruger.id === brugerId 
          ? { ...bruger, active: false }
          : bruger
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleActivateBruger = async (brugerId) => {
    try {
      await adminApi.activateClientUser(user.clientId, brugerId);
      setBrugere(brugere.map(bruger => 
        bruger.id === brugerId 
          ? { ...bruger, active: true }
          : bruger
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="brugeradgang-container">
      <div className="brugeradgang-header">
        <h3>Brugeradgang</h3>
        <button onClick={() => setShowAddModal(true)} className="add-button">
          <i className="fas fa-plus"></i> Tilføj ny bruger
        </button>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Navn</th>
            <th>E-mail</th>
            <th>Rettigheder</th>
            <th>Status</th>
            <th>Handling</th>
          </tr>
        </thead>
        <tbody>
          {brugere.map(bruger => (
            <tr key={bruger.id}>
              <td>{bruger.name}</td>
              <td>{bruger.email}</td>
              <td>{translateRole(bruger.role)}</td>
              <td>
                <span className={bruger.active ? 'aktiv' : 'inaktiv'}>
                  {bruger.active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </td>
              <td>
                {bruger.active ? (
                  <button 
                    onClick={() => handleDeactivateBruger(bruger.id)}
                    className="deaktiver-button"
                  >
                    Deaktiver
                  </button>
                ) : (
                  <button 
                    onClick={() => handleActivateBruger(bruger.id)}
                    className="aktiver-button"
                  >
                    Aktivér
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {showAddModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Tilføj ny bruger</h4>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleAddBruger();
            }}>
              <div className="form-group">
                <label>Navn:</label>
                <input
                  type="text"
                  name="name"
                  value={newBruger.name}
                  onChange={(e) => setNewBruger({...newBruger, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>E-mail:</label>
                <input
                  type="email"
                  name="email"
                  value={newBruger.email}
                  onChange={(e) => setNewBruger({...newBruger, email: e.target.value})}
                  required
                />
              </div>
              <div className="button-group">
                <button type="submit" className="save-button">Gem</button>
                <button type="button" onClick={() => setShowAddModal(false)} className="cancel-button">Annuller</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function AfregningsformTab() {
  const [afregningsformer, setAfregningsformer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingFormId, setDeletingFormId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchAfregningsformer = async () => {
      try {
        const data = await api.get('/payment-forms');
        setAfregningsformer(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAfregningsformer();
  }, []);

  const handleAdd = () => {
    setEditingForm({
      name: '',
      short_name: '',
      disposition_rule: ''
    });
    setIsAdding(true);
    setShowModal(true);
  };

  const handleEdit = (form) => {
    setEditingForm(form);
    setIsAdding(false);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      let data;
      if (isAdding) {
        data = await api.post('/payment-forms', editingForm);
        setAfregningsformer([...afregningsformer, data]);
      } else {
        data = await api.put(`/payment-forms/${editingForm.id}`, editingForm);
        setAfregningsformer(afregningsformer.map(f => 
          f.id === data.id ? data : f
        ));
      }
      setShowModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    setDeletingFormId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/payment-forms/${deletingFormId}`);
      setAfregningsformer(afregningsformer.filter(f => f.id !== deletingFormId));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeletingFormId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingFormId(null);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div>
      <div className="brugeradgang-header">
        <h3>Afregningsform</h3>
        <button onClick={handleAdd} className="add-button">
          <i className="fas fa-plus"></i> Tilføj ny afregningsform
        </button>
      </div>

      <table className="afregningsform-table">
        <thead>
          <tr>
            <th>Navn</th>
            <th>Forkortelse</th>
            <th>Disposition</th>
            <th>Handling</th>
          </tr>
        </thead>
        <tbody>
          {afregningsformer.map((form) => (
            <tr key={form.id}>
              <td>{form.name}</td>
              <td>{form.short_name}</td>
              <td>{form.disposition_rule}</td>
              <td>
                <div className="button-group">
                  <button 
                    className="button button-secondary"
                    onClick={() => handleEdit(form)}
                  >
                    <i className="fas fa-edit"></i> Rediger
                  </button>
                  <button 
                    className="button button-warning"
                    onClick={() => handleDelete(form.id)}
                  >
                    <i className="fas fa-trash"></i> Slet
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>{isAdding ? 'Tilføj ny afregningsform' : 'Rediger afregningsform'}</h4>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}>
              <div className="form-group">
                <label>Navn:</label>
                <input
                  type="text"
                  name="name"
                  value={editingForm.name}
                  onChange={(e) => setEditingForm({...editingForm, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Forkortelse:</label>
                <input
                  type="text"
                  name="short_name"
                  value={editingForm.short_name}
                  onChange={(e) => setEditingForm({...editingForm, short_name: e.target.value})}
                  required
                  maxLength={4}
                />
              </div>
              <div className="form-group">
                <label>Dispositionsregel:</label>
                <input
                  type="text"
                  name="disposition_rule"
                  value={editingForm.disposition_rule}
                  onChange={(e) => setEditingForm({...editingForm, disposition_rule: e.target.value})}
                  required
                />
              </div>
              <div className="button-group">
                <button type="button" onClick={() => setShowModal(false)} className="cancel-button">
                  Annuller
                </button>
                <button type="submit" className="save-button">
                  {isAdding ? 'Tilføj' : 'Gem ændringer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay active">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Slet afregningsform</h4>
              <button className="modal-close-button" onClick={handleCancelDelete}>×</button>
            </div>
            <div className="modal-body">
              <p>Er du sikker på, at du vil slette denne afregningsform?</p>
              <p className="warning-text">Denne handling kan ikke fortrydes.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="button button-secondary"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Annuller
              </button>
              <button 
                type="button" 
                className="button button-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Sletter...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i> Slet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MedarbejderfelterTab() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dropdownLists, setDropdownLists] = useState([]);
  const [editingDropdownListId, setEditingDropdownListId] = useState(null);
  const [dropdownItems, setDropdownItems] = useState([]);
  const [showDropdownModal, setShowDropdownModal] = useState(false);
  const [newDropdownItem, setNewDropdownItem] = useState({ value: '', display_text: '', sort_order: 0 });
  const [isDropdownSaving, setIsDropdownSaving] = useState(false);
  const [showNewDropdownModal, setShowNewDropdownModal] = useState(false);
  const [newDropdownListName, setNewDropdownListName] = useState('');
  const [newDropdownFieldName, setNewDropdownFieldName] = useState(null);
  const [isCreatingDropdown, setIsCreatingDropdown] = useState(false);

  useEffect(() => {
    const fetchFieldsAndDropdowns = async () => {
      try {
        const [fieldsData, listsData] = await Promise.all([
          api.get('/employee-fields'),
          api.get('/dropdown-lists')
        ]);
        setFields(fieldsData);
        setDropdownLists(listsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchFieldsAndDropdowns();
  }, []);

  const handleFieldChange = async (fieldName, property, value) => {
    try {
      setIsSaving(true);
      await api.put(`/employee-fields/${fieldName}`, {
        [property]: value
      });
      setFields(fields.map(field =>
        field.field_name === fieldName
          ? { ...field, [property]: value }
          : field
      ));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldTypeChange = async (fieldName, value) => {
    await handleFieldChange(fieldName, 'field_type', value);
    if (value !== 'dropdown') {
      await handleFieldChange(fieldName, 'dropdown_list_id', null);
    }
  };

  const handleDropdownListChange = async (fieldName, value) => {
    if (value === '__new__') {
      setShowNewDropdownModal(true);
      setNewDropdownListName('');
      setNewDropdownFieldName(fieldName);
      return;
    }
    await handleFieldChange(fieldName, 'dropdown_list_id', value);
  };

  const openDropdownModal = async (dropdownListId) => {
    setEditingDropdownListId(dropdownListId);
    setShowDropdownModal(true);
    setIsDropdownSaving(true);
    try {
      const items = await api.get(`/dropdown-lists/${dropdownListId}/items`);
      setDropdownItems(items);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDropdownSaving(false);
    }
  };

  const handleAddDropdownItem = async (e) => {
    e.preventDefault();
    setIsDropdownSaving(true);
    try {
      const item = await api.post('/dropdown-list-items', {
        ...newDropdownItem,
        dropdown_list_id: editingDropdownListId
      });
      setDropdownItems([...dropdownItems, item]);
      setNewDropdownItem({ value: '', display_text: '', sort_order: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDropdownSaving(false);
    }
  };

  const handleDeleteDropdownItem = async (itemId) => {
    setIsDropdownSaving(true);
    try {
      await api.delete(`/dropdown-list-items/${itemId}`);
      setDropdownItems(dropdownItems.filter(i => i.id !== itemId));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDropdownSaving(false);
    }
  };

  const handleCreateDropdownList = async (e) => {
    e.preventDefault();
    if (!newDropdownListName.trim()) return;
    setIsCreatingDropdown(true);
    try {
      const newList = await api.post('/dropdown-lists', { name: newDropdownListName });
      setDropdownLists([...dropdownLists, newList]);
      setShowNewDropdownModal(false);
      setIsCreatingDropdown(false);
      setNewDropdownListName('');
      if (newDropdownFieldName) {
        await handleFieldChange(newDropdownFieldName, 'dropdown_list_id', newList.id);
        setFields(fields.map(f =>
          f.field_name === newDropdownFieldName ? { ...f, dropdown_list_id: newList.id } : f
        ));
      }
      setNewDropdownFieldName(null);
    } catch (err) {
      setError(err.message);
      setIsCreatingDropdown(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  // Gruppér felter efter sektion
  const fieldsBySection = fields.reduce((acc, field) => {
    if (!acc[field.section]) {
      acc[field.section] = [];
    }
    acc[field.section].push(field);
    return acc;
  }, {});

  return (
    <div className="medarbejderfelter-container">
      <div className="medarbejderfelter-header">
        <h3>Medarbejderfelter</h3>
        <p className="section-description">
          Her kan du styre hvilke felter der skal vises i medarbejderformularen.
        </p>
      </div>

      {Object.entries(fieldsBySection).map(([section, sectionFields]) => (
        <div key={section} className="section-container">
          <h4>{SECTION_LABELS[section] || section}</h4>
          <table>
            <thead>
              <tr>
                <th>Felt</th>
                <th>Felttype</th>
                <th>Dropdown-liste</th>
                <th>Lønsystem</th>
                <th>Standardværdi</th>
                <th>Maks. længde</th>
                <th>Hjælpetekst</th>
                <th>Aktiv</th>
                <th>Påkrævet</th>
              </tr>
            </thead>
            <tbody>
              {sectionFields.map((field) => (
                <tr key={field.field_name}>
                  <td>{field.display_name}</td>
                  <td>
                    <select
                      value={field.field_type || 'text'}
                      onChange={e => handleFieldTypeChange(field.field_name, e.target.value)}
                      disabled={isSaving}
                    >
                      <option value="text">Tekst</option>
                      <option value="date">Dato</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="boolean">Afkrydsning</option>
                      <option value="number">Tal</option>
                      <option value="email">E-mail</option>
                    </select>
                  </td>
                  <td>
                    {field.field_type === 'dropdown' && (
                      <>
                        <select
                          value={field.dropdown_list_id || ''}
                          onChange={e => handleDropdownListChange(field.field_name, e.target.value)}
                          disabled={isSaving}
                        >
                          <option value="">Vælg dropdown-liste</option>
                          {dropdownLists.map(list => (
                            <option key={list.id} value={list.id}>{list.name}</option>
                          ))}
                          <option value="__new__">Opret ny liste...</option>
                        </select>
                        {field.dropdown_list_id && (
                          <button
                            type="button"
                            className="button button-secondary"
                            onClick={() => openDropdownModal(field.dropdown_list_id)}
                            style={{ marginLeft: 8 }}
                          >
                            Rediger liste
                          </button>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <select
                      value={field.payroll_system || 'all'}
                      onChange={e => handleFieldChange(field.field_name, 'payroll_system', e.target.value)}
                      disabled={isSaving}
                    >
                      <option value="all">Alle</option>
                      <option value="zenegy">Zenegy</option>
                      <option value="lessor">Lessor</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={field.default_value || ''}
                      onChange={e => handleFieldChange(field.field_name, 'default_value', e.target.value)}
                      disabled={isSaving}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={field.max_length || ''}
                      onChange={e => handleFieldChange(field.field_name, 'max_length', e.target.value)}
                      disabled={isSaving}
                      min="0"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={field.help_text || ''}
                      onChange={e => handleFieldChange(field.field_name, 'help_text', e.target.value)}
                      disabled={isSaving}
                    />
                  </td>
                  <td>
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        checked={field.is_active}
                        onChange={e => handleFieldChange(field.field_name, 'is_active', e.target.checked)}
                        disabled={isSaving}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        checked={field.is_required}
                        onChange={e => handleFieldChange(field.field_name, 'is_required', e.target.checked)}
                        disabled={isSaving || !field.is_active}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {showDropdownModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Rediger dropdown-liste</h4>
            <form onSubmit={handleAddDropdownItem} style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Værdi"
                value={newDropdownItem.value}
                onChange={e => setNewDropdownItem({ ...newDropdownItem, value: e.target.value })}
                required
                style={{ marginRight: 8 }}
              />
              <input
                type="text"
                placeholder="Visningstekst"
                value={newDropdownItem.display_text}
                onChange={e => setNewDropdownItem({ ...newDropdownItem, display_text: e.target.value })}
                required
                style={{ marginRight: 8 }}
              />
              <input
                type="number"
                placeholder="Sortering"
                value={newDropdownItem.sort_order}
                onChange={e => setNewDropdownItem({ ...newDropdownItem, sort_order: Number(e.target.value) })}
                style={{ width: 80, marginRight: 8 }}
              />
              <button type="submit" className="save-button" disabled={isDropdownSaving}>Tilføj</button>
              <button type="button" className="cancel-button" onClick={() => setShowDropdownModal(false)} style={{ marginLeft: 8 }}>Luk</button>
            </form>
            <table>
              <thead>
                <tr>
                  <th>Værdi</th>
                  <th>Visningstekst</th>
                  <th>Sortering</th>
                  <th>Handling</th>
                </tr>
              </thead>
              <tbody>
                {dropdownItems.map(item => (
                  <tr key={item.id}>
                    <td>{item.value}</td>
                    <td>{item.display_text}</td>
                    <td>{item.sort_order}</td>
                    <td>
                      <button
                        type="button"
                        className="button button-warning"
                        onClick={() => handleDeleteDropdownItem(item.id)}
                        disabled={isDropdownSaving}
                      >
                        Slet
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showNewDropdownModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Opret ny dropdown-liste</h4>
            <form onSubmit={handleCreateDropdownList}>
              <input
                type="text"
                placeholder="Navn på ny liste"
                value={newDropdownListName}
                onChange={e => setNewDropdownListName(e.target.value)}
                required
                style={{ marginRight: 8 }}
              />
              <button type="submit" className="save-button" disabled={isCreatingDropdown}>Opret</button>
              <button type="button" className="cancel-button" onClick={() => setShowNewDropdownModal(false)} style={{ marginLeft: 8 }}>Annuller</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function LonkorslerTab() {
  const [lonkorsler, setLonkorsler] = useState([]);
  const [editingKorsel, setEditingKorsel] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [afregningsformer, setAfregningsformer] = useState([]);
  const [closingPayRunId, setClosingPayRunId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [payRunsData, paymentFormsData] = await Promise.all([
        api.get('/pay-runs'),
        api.get('/payment-forms')
      ]);
      setLonkorsler(payRunsData);
      setAfregningsformer(paymentFormsData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (korsel) => {
    setEditingKorsel({
      ...korsel,
      disposition_date: formatDate(korsel.disposition_date),
      input_deadline: formatDate(korsel.input_deadline),
      approval_material_deadline: formatDate(korsel.approval_material_deadline),
      approval_deadline: formatDate(korsel.approval_deadline),
      output_deadline: formatDate(korsel.output_deadline)
    });
    setIsAdding(false);
    setIsModalOpen(true);
    setError('');
  };

  const handleAdd = () => {
    const today = new Date();
    setEditingKorsel({
      number: "",
      description: "",
      payment_form_id: "",
      month: today.getMonth() + 1,
      year: today.getFullYear(),
      disposition_date: "",
      input_deadline: "",
      approval_material_deadline: "",
      approval_deadline: "",
      output_deadline: "",
      status: STATUS_TYPES.PENDING
    });
    setIsAdding(true);
    setIsModalOpen(true);
    setError('');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      const payload = {
        ...editingKorsel,
        month: parseInt(editingKorsel.month),
        year: parseInt(editingKorsel.year),
        payment_form_id: parseInt(editingKorsel.payment_form_id),
        disposition_date: formatDateForServer(editingKorsel.disposition_date),
        input_deadline: formatDateForServer(editingKorsel.input_deadline),
        approval_material_deadline: formatDateForServer(editingKorsel.approval_material_deadline),
        approval_deadline: formatDateForServer(editingKorsel.approval_deadline),
        output_deadline: formatDateForServer(editingKorsel.output_deadline)
      };

      if (isAdding) {
        await api.createPayRun(payload);
      } else {
        await api.put(`/pay-runs/${editingKorsel.id}`, payload);
      }

      await fetchData();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLukKorsel = async (korselId) => {
    try {
      setClosingPayRunId(korselId);
      await api.post(`/pay-runs/${korselId}/close`);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setClosingPayRunId(null);
    }
  };

  const handleCloseModal = () => {
    setEditingKorsel(null);
    setIsAdding(false);
    setIsModalOpen(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingKorsel(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="brugeradgang-header">
        <h3>Lønkørsler</h3>
        <button className="add-button" onClick={handleAdd}>
          <i className="fas fa-plus"></i> Tilføj ny lønkørsel
        </button>
      </div>
      {error && <ErrorMessage message={error} />}
      
      <table className="lonkorsler-table">
        <thead>
          <tr>
            <th>Lønkørselsnummer</th>
            <th>Betegnelse</th>
            <th>Afregningsform</th>
            <th>Status</th>
            <th>Handling</th>
          </tr>
        </thead>
        <tbody>
          {lonkorsler.map((korsel) => (
            <tr key={korsel.id}>
              <td>{korsel.number}</td>
              <td>{korsel.description}</td>
              <td>{korsel.payment_form_name}</td>
              <td>
                <span className={`status-badge ${korsel.status.toLowerCase()}`}>
                  {korsel.status}
                </span>
              </td>
              <td>
                <div className="button-group">
                  <button 
                    className="button button-secondary"
                    onClick={() => handleEdit(korsel)}
                  >
                    <i className="fas fa-edit"></i> Rediger
                  </button>
                  {korsel.status === STATUS_TYPES.ACTIVE && (
                    <button 
                      className="button button-warning"
                      onClick={() => handleLukKorsel(korsel.id)}
                      disabled={closingPayRunId === korsel.id}
                    >
                      {closingPayRunId === korsel.id ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Lukker...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-lock"></i> Luk lønkørsel
                        </>
                      )}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isModalOpen && (
        <div className={`modal-overlay ${isModalOpen ? 'active' : ''}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h4>{isAdding ? 'Tilføj ny lønkørsel' : 'Rediger lønkørsel'}</h4>
              <button className="modal-close-button" onClick={handleCloseModal}>×</button>
            </div>
            
            <form className="lonkorsel-form">
              {error && <ErrorMessage message={error} />}
              
              {/* Venstre kolonne - Basis information */}
              <div className="lonkorsel-form-section">
                <h5>Grundlæggende oplysninger</h5>
                <div className="basic-info-section">
                  <div className="form-group">
                    <label>Lønkørselsnummer:</label>
                    <input 
                      type="text" 
                      name="number" 
                      value={editingKorsel.number} 
                      onChange={handleChange} 
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Betegnelse:</label>
                    <input 
                      type="text" 
                      name="description" 
                      value={editingKorsel.description} 
                      onChange={handleChange} 
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Afregningsform:</label>
                    <select 
                      name="payment_form_id" 
                      value={editingKorsel.payment_form_id} 
                      onChange={handleChange}
                      required
                      className="status-select"
                    >
                      <option value="">Vælg afregningsform</option>
                      {afregningsformer.map((form) => (
                        <option key={form.id} value={form.id}>
                          {form.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status:</label>
                    <select 
                      name="status" 
                      value={editingKorsel.status} 
                      onChange={handleChange}
                      required
                      className="status-select"
                    >
                      <option value={STATUS_TYPES.PENDING}>{STATUS_TYPES.PENDING}</option>
                      <option value={STATUS_TYPES.ACTIVE}>{STATUS_TYPES.ACTIVE}</option>
                      <option value={STATUS_TYPES.CLOSED}>{STATUS_TYPES.CLOSED}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Måned:</label>
                    <select 
                      name="month" 
                      value={editingKorsel.month} 
                      onChange={handleChange}
                      required
                      className="month-select"
                    >
                      {MONTHS.map((month, index) => (
                        <option key={index + 1} value={index + 1}>{month}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>År:</label>
                    <input 
                      type="number" 
                      name="year" 
                      value={editingKorsel.year} 
                      onChange={handleChange}
                      min="2024"
                      max="2099"
                      required
                      className="year-input"
                    />
                  </div>
                </div>
              </div>

              {/* Højre kolonne - Deadlines */}
              <div className="lonkorsel-form-section">
                <h5>Deadlines</h5>
                <div className="deadline-fields">
                  <div className="form-group">
                    <label>Dispositionsdato:</label>
                    <input 
                      type="date" 
                      name="disposition_date" 
                      value={editingKorsel.disposition_date} 
                      onChange={handleChange} 
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Deadline for input:</label>
                    <input 
                      type="date" 
                      name="input_deadline" 
                      value={editingKorsel.input_deadline} 
                      onChange={handleChange} 
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Deadline for godkendelsesmateriale:</label>
                    <input 
                      type="date" 
                      name="approval_material_deadline" 
                      value={editingKorsel.approval_material_deadline} 
                      onChange={handleChange} 
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Deadline for godkendelse:</label>
                    <input 
                      type="date" 
                      name="approval_deadline" 
                      value={editingKorsel.approval_deadline} 
                      onChange={handleChange} 
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Deadline for outputfiler:</label>
                    <input 
                      type="date" 
                      name="output_deadline" 
                      value={editingKorsel.output_deadline} 
                      onChange={handleChange} 
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Footer med knapper */}
              <div className="form-footer">
                <button 
                  type="button" 
                  className="button button-secondary"
                  onClick={handleCloseModal}
                  disabled={isSaving}
                >
                  <i className="fas fa-times"></i>
                  Annuller
                </button>
                <button 
                  type="button" 
                  className="button button-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Gemmer...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      {isAdding ? 'Tilføj' : 'Gem ændringer'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function LonarterTab() {
  const [lonarter, setLonarter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLonart, setEditingLonart] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchLonarter = async () => {
      try {
        const data = await api.getPayrollTypes();
        setLonarter(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLonarter();
  }, []);

  const handleAdd = () => {
    setEditingLonart({
      code: '',
      name: '',
      description: '',
      is_active: true
    });
    setIsAdding(true);
    setShowModal(true);
  };

  const handleEdit = (lonart) => {
    setEditingLonart(lonart);
    setIsAdding(false);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      let data;
      if (isAdding) {
        data = await api.createPayrollType(editingLonart);
        setLonarter([...lonarter, data]);
      } else {
        data = await api.updatePayrollType(editingLonart.id, editingLonart);
        setLonarter(lonarter.map(l => l.id === data.id ? data : l));
      }
      setShowModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne lønart?')) {
      try {
        await api.deletePayrollType(id);
        setLonarter(lonarter.filter(l => l.id !== id));
      } catch (err) {
        setError(err.message);
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="lonarter-container">
      <div className="lonarter-header">
        <h3>Lønarter</h3>
        <button onClick={handleAdd} className="add-button">
          <i className="fas fa-plus"></i> Tilføj ny lønart
        </button>
      </div>

      <table className="lonarter-table">
        <thead>
          <tr>
            <th>Kode</th>
            <th>Navn</th>
            <th>Beskrivelse</th>
            <th>Status</th>
            <th>Handling</th>
          </tr>
        </thead>
        <tbody>
          {lonarter.map((lonart) => (
            <tr key={lonart.id}>
              <td>{lonart.code}</td>
              <td>{lonart.name}</td>
              <td>{lonart.description}</td>
              <td>
                <span className={`status-badge ${lonart.is_active ? 'aktiv' : 'inaktiv'}`}>
                  {lonart.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </td>
              <td>
                <div className="button-group">
                  <button 
                    className="button button-secondary"
                    onClick={() => handleEdit(lonart)}
                  >
                    <i className="fas fa-edit"></i> Rediger
                  </button>
                  <button 
                    className="button button-warning"
                    onClick={() => handleDelete(lonart.id)}
                  >
                    <i className="fas fa-trash"></i> Slet
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>{isAdding ? 'Tilføj ny lønart' : 'Rediger lønart'}</h4>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}>
              <div className="form-group">
                <label>Kode:</label>
                <input
                  type="text"
                  value={editingLonart.code}
                  onChange={(e) => setEditingLonart({...editingLonart, code: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Navn:</label>
                <input
                  type="text"
                  value={editingLonart.name}
                  onChange={(e) => setEditingLonart({...editingLonart, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beskrivelse:</label>
                <textarea
                  value={editingLonart.description}
                  onChange={(e) => setEditingLonart({...editingLonart, description: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editingLonart.is_active}
                    onChange={(e) => setEditingLonart({...editingLonart, is_active: e.target.checked})}
                  />
                  Aktiv
                </label>
              </div>
              <div className="button-group">
                <button type="button" onClick={() => setShowModal(false)} className="cancel-button">
                  Annuller
                </button>
                <button type="submit" className="save-button" disabled={isSaving}>
                  {isSaving ? 'Gemmer...' : (isAdding ? 'Tilføj' : 'Gem ændringer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SkabelonerTab() {
  const [skabeloner, setSkabeloner] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSkabelon, setEditingSkabelon] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSkabelonId, setDeletingSkabelonId] = useState(null);

  useEffect(() => {
    const fetchSkabeloner = async () => {
      try {
        const data = await api.getEmployeeTemplates();
        setSkabeloner(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSkabeloner();
  }, []);

  const handleAdd = () => {
    setEditingSkabelon({
      name: '',
      description: '',
      company: '',
      department: '',
      dimension1: '',
      dimension2: '',
      dimension3: '',
      title: '',
      salary_group: '',
      employment_type: '',
      position_profile: '',
      job_function_code: '',
      accounting_group_code: '',
      salary: '',
      hourly_rate: '',
      monthly_hours: '',
      normal_hours: '',
      pension: '',
      free_phone: false,
      free_phone_amount: '',
      free_car: false,
      health_insurance: false,
      staff_association: false,
      lunch_arrangement: false,
      care_days: '',
      vacation_days: '',
      free_choice: false,
      other: '',
      ft_percentage: '',
      weekly_hours: ''
    });
    setIsAdding(true);
    setShowModal(true);
  };

  const handleEdit = (skabelon) => {
    setEditingSkabelon(skabelon);
    setIsAdding(false);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeletingSkabelonId(id);
    setShowDeleteModal(true);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="skabeloner-container">
      <div className="skabeloner-header">
        <h3>Medarbejderskabeloner</h3>
        <button onClick={handleAdd} className="add-button">
          <i className="fas fa-plus"></i> Tilføj ny skabelon
        </button>
      </div>

      <table className="skabeloner-table">
        <thead>
          <tr>
            <th>Navn</th>
            <th>Beskrivelse</th>
            <th>Afdeling</th>
            <th>Handling</th>
          </tr>
        </thead>
        <tbody>
          {skabeloner.map((skabelon) => (
            <tr key={skabelon.id}>
              <td>{skabelon.name}</td>
              <td>{skabelon.description}</td>
              <td>{skabelon.department}</td>
              <td>
                <div className="button-group">
                  <button 
                    className="button button-secondary"
                    onClick={() => handleEdit(skabelon)}
                  >
                    <i className="fas fa-edit"></i> Rediger
                  </button>
                  <button 
                    className="button button-warning"
                    onClick={() => handleDelete(skabelon.id)}
                  >
                    <i className="fas fa-trash"></i> Slet
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="employee-modal-backdrop">
          <div className="employee-modal">
            <button 
              className="close-button"
              onClick={() => setShowModal(false)}
              title="Luk"
            >
              ×
            </button>
            <h2>{isAdding ? 'Tilføj ny skabelon' : 'Rediger skabelon'}</h2>
            <div className="employee-modal-content">
              <EmployeeTemplateForm
                initialValues={editingSkabelon}
                onSave={async (values) => {
                  setIsSaving(true);
                  try {
                    let data;
                    if (isAdding) {
                      data = await api.createEmployeeTemplate(values);
                      setSkabeloner([...skabeloner, data]);
                    } else {
                      data = await api.updateEmployeeTemplate(editingSkabelon.id, values);
                      setSkabeloner(skabeloner.map(s => s.id === data.id ? data : s));
                    }
                    setShowModal(false);
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setIsSaving(false);
                  }
                }}
                onCancel={() => setShowModal(false)}
                isSaving={isSaving}
              />
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay active">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Slet skabelon</h4>
              <button className="modal-close-button" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Er du sikker på, at du vil slette denne skabelon?</p>
              <p className="warning-text">Denne handling kan ikke fortrydes.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="button button-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuller
              </button>
              <button 
                type="button" 
                className="button button-danger"
                onClick={() => {
                  setIsSaving(true);
                  api.deleteEmployeeTemplate(deletingSkabelonId)
                    .then(() => {
                      setSkabeloner(skabeloner.filter(s => s.id !== deletingSkabelonId));
                      setShowDeleteModal(false);
                    })
                    .catch(err => {
                      setError(err.message);
                    })
                    .finally(() => {
                      setIsSaving(false);
                    });
                }}
              >
                <i className="fas fa-trash"></i> Slet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
